// admin/src/pages/NoteForm.tsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createNote, updateNote, getNoteById,
  listProjectsForUser, createProject,
  fetchUsers, getUserById,
} from '../services/api';
import {
  Typography, TextField, Button, Paper, Box, Stack, Autocomplete,
  IconButton, Tooltip, Chip, Divider, Collapse, Link as MLink,
  Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import UploadRounded from '@mui/icons-material/UploadRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import Swal from 'sweetalert2';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  Alignment,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  Heading,
  Italic,
  Link,
  List,
  Paragraph,
  Strikethrough,
  Table,
  TableToolbar,
  Underline,
  Undo,
} from 'ckeditor5';

/* ==================== Types & utils ==================== */
type OptionItem = { _id: string; name: string; [k: string]: any };
type NotePayload = {
  title: string; note: string;
  entryDate?: string | null;
  project?: string | null; user?: string | null;
  category?: string | null; tags?: string[];
};
type NoteFile = { _id: string; url: string; filename?: string; size?: number; mimetype?: string };
type User = { _id: string; username?: string; email?: string; fullName?: string; firstName?: string; lastName?: string };

const CATEGORY_OPTIONS = ['Personal', 'Work', 'Ideas', 'Meeting', 'Others'];

const API_BASE = process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api';
const FILES_BASE = API_BASE.replace(/\/api\/?$/, '');
const toFileUrl = (u?: string) => (u ? (/^https?:\/\//i.test(u) ? u : `${FILES_BASE}${u}`) : '');

const nowLocalDateTime = () => {
  const d = new Date(); const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const asLocalInput = (val?: string | Date | null) => {
  if (!val) return ''; const d = new Date(val); if (isNaN(d.getTime())) return '';
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const stripHtml = (html: string) => {
  const div = document.createElement('div'); div.innerHTML = html;
  const blockTags = new Set(['P','DIV','LI','BR','H1','H2','H3','H4','H5','H6']);
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_ELEMENT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.nodeType === 1 && blockTags.has((node as HTMLElement).tagName)) (node as HTMLElement).insertAdjacentText('beforebegin', '\n');
  }
  const text = div.textContent || ''; return text.replace(/\n{3,}/g, '\n\n').trim();
};
const wrapPlainAsHtml = (txt: string) => !txt.trim() ? '' : (/<\/?[a-z][\s\S]*>/i.test(txt) ? txt : `<p>${txt.replace(/\n/g, '<br/>')}</p>`);

const LS_KEY_RTE = 'qs_rich_editor';
const normalizeOptions = (arr: any[]): OptionItem[] =>
  (arr || []).map((x: any) =>
    typeof x === 'string'
      ? { _id: x, name: x }
      : { _id: x?._id || x?.id || x?.value || x?.name, name: x?.name || x?.label || x?.title || '' }
  ).filter(o => o._id && o.name);
const findById = (opts: OptionItem[], id?: string | null) => (id ? opts.find(o => o._id === id) || null : null);
const makeUserOptions = (list: User[]): OptionItem[] =>
  (list || []).map(u => ({
    _id: u._id,
    name: (u.username || u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u._id)
  }));

const unwrapList = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const ckEditorLicenseKey = process.env.REACT_APP_CKEDITOR_LICENSE_KEY || 'GPL';

const ckLiteConfig = {
  licenseKey: ckEditorLicenseKey,
  plugins: [
    Alignment,
    BlockQuote,
    Bold,
    Essentials,
    Heading,
    Italic,
    Link,
    List,
    Paragraph,
    Strikethrough,
    Table,
    TableToolbar,
    Underline,
    Undo,
  ],
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
  toolbar: [
    'heading', '|',
    'bold', 'italic', '|',
    'numberedList', 'bulletedList', '|',
    'blockQuote', 'link', 'insertTable', 'undo', 'redo',
  ],
};

/* ==================== Add Project Dialog ==================== */
function AddProjectDialog({
  open, onClose, onCreate, options, onSelectExisting,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
  options: OptionItem[];
  onSelectExisting: (opt: OptionItem) => void;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setName(''); setErr(''); setSaving(false); }
  }, [open]);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) return setErr('Required');
    const existing = options.find(o => o.name.toLowerCase().trim() === clean.toLowerCase().trim());
    if (existing) { onSelectExisting(existing); onClose(); return; }
    setSaving(true);
    try {
      await onCreate(clean);
      onClose();
    }
    catch (e: any) { setErr(e?.response?.data?.message || 'Failed to add'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Project</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          label="Project name"
          value={name}
          onChange={(e) => { setName(e.target.value); setErr(''); }}
          fullWidth
          error={!!err}
          helperText={err || ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===== CK Lite ===== */
const CKLite: React.FC<{ value: string; onChange: (html: string) => void }> = ({ value, onChange }) => {
  const editorRef = useRef<any>(null);
  const pastedRef = useRef(false);
  const lastValueRef = useRef<string>(value || '');
  const moveCaretToEnd = useCallback(() => {
    const editor = editorRef.current; if (!editor) return;
    const model = editor.model;
    model.change((writer: any) => {
      const root = model.document.getRoot()!; const pos = model.createPositionAt(root, 'end'); writer.setSelection(pos);
    });
    editor.editing.view.focus();
    setTimeout(() => { const domRoot = editor.ui.getEditableElement(); if (domRoot) domRoot.scrollTop = domRoot.scrollHeight; }, 0);
  }, []);
  return (
    <CKEditor
      editor={ClassicEditor}
      data={value || ''}
      onReady={(editor: any) => {
        editorRef.current = editor;
        const viewDoc = editor.editing.view.document;
        viewDoc.on('clipboardInput', () => { pastedRef.current = true; });
        editor.model.document.on('change:data', () => {
          const html = editor.getData(); lastValueRef.current = html; onChange(html);
          if (pastedRef.current) { requestAnimationFrame(() => { moveCaretToEnd(); pastedRef.current = false; }); }
        });
        requestAnimationFrame(moveCaretToEnd);
      }}
      onChange={(_: any, editor: any) => {
        const html = editor.getData(); if (html !== lastValueRef.current) { lastValueRef.current = html; onChange(html); }
      }}
      config={ckLiteConfig}
    />
  );
};

/* ==================== Component ==================== */
const NoteForm: React.FC = () => {
  const { noteSlug } = useParams();
  const navigate = useNavigate();

  // Primary
  const [title, setTitle] = useState('');
  const [noteHtml, setNoteHtml] = useState('');
  const [noteText, setNoteText] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [projectSel, setProjectSel] = useState<OptionItem | null>(null);

  // Admin: user selection (REQUIRED)
  const [userOptions, setUserOptions] = useState<OptionItem[]>([]);
  const [userSel, setUserSel] = useState<OptionItem | null>(null);

  // Scoped options
  const [projectOptions, setProjectOptions] = useState<OptionItem[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Files
  const [existingFiles, setExistingFiles] = useState<NoteFile[]>([]);
  const [removeFileIds, setRemoveFileIds] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newPreviews, setNewPreviews] = useState<string[]>([]);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [useEditor, setUseEditor] = useState<boolean>(() => (localStorage.getItem(LS_KEY_RTE) === '1'));
  const [addProjectOpen, setAddProjectOpen] = useState(false);

  // Load user list only (NO global lists)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchUsers();
        let arr: User[] = [];
        if (Array.isArray(res)) arr = res;
        else if (res?.data && Array.isArray(res.data)) arr = res.data;
        else if (res?.items && Array.isArray(res.items)) arr = res.items;
        else if (res?.users && Array.isArray(res.users)) arr = res.users;
        setUserOptions(makeUserOptions(arr));
      } catch (e) {
        console.warn('fetchUsers failed', e);
        setUserOptions([]);
      }
    })();

    if (!noteSlug) setEntryDate(nowLocalDateTime());
  }, [noteSlug]);

  // Race-safe scoped loader
  const fetchScopedLists = useCallback(async (uid: string) => {
    const token = uid + ':' + Date.now();
    (fetchScopedLists as any)._token = token;

    try {
      const psRaw = await listProjectsForUser(uid);
      if ((fetchScopedLists as any)._token !== token) {
        return { projects: [] };
      }
      const projects = normalizeOptions(unwrapList(psRaw));
      setProjectOptions(projects);
      return { projects };
    } catch (e: any) {
      console.warn('fetchScopedLists failed', e);
      setProjectOptions([]);

      if (e?.response?.status === 403) {
        Swal.fire(
          'Access denied',
          'These lists are admin-scoped. Enable admin access or set ALLOW_ADMIN_SCOPED_FOR_ALL=1 on the server while developing.',
          'info'
        );
      } else {
        Swal.fire('Error', e?.response?.data?.message || 'Failed to load user-scoped lists.', 'error');
      }

      return { projects: [] };
    }
  }, []);

  // On user change
  const onUserChange = async (_: any, v: OptionItem | null) => {
    setUserSel(v);
    setErrors(p => ({ ...p, user: '' }));

    setProjectSel(null);
    setProjectOptions([]);

    if (!v?._id) return;
    await fetchScopedLists(v._id);
  };

  // Editing existing note — load user → scoped lists → set selections from those lists
  useEffect(() => {
    (async () => {
      if (!noteSlug) return;
      try {
        const n = await getNoteById(noteSlug);
        const incoming = n.note ?? n.content ?? '';

        setTitle(n.title ?? '');
        setEntryDate(asLocalInput(n.entryDate));
        setCategory(n.category || '');
        setTags(Array.isArray(n.tags) ? n.tags : []);
        setExistingFiles((n.images || []) as NoteFile[]);

        const uid = typeof n.user === 'string' ? n.user : (n.user?._id || n.user?.id || '');
        if (uid) {
          try {
            const ud = await getUserById(uid);
            const label = (ud?.username || ud?.fullName || [ud?.firstName, ud?.lastName].filter(Boolean).join(' ') || ud?.email || uid);
            setUserSel({ _id: uid, name: label });
          } catch {
            setUserSel({ _id: uid, name: uid });
          }

          const { projects } = await fetchScopedLists(uid);

          if (n.project) {
            const pid = typeof n.project === 'string' ? n.project : (n.project._id || n.project.id);
            setProjectSel(findById(projects, pid));
          }
        }

        const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(incoming);
        if (looksLikeHtml) {
          setNoteHtml(incoming);
          setNoteText(stripHtml(incoming));
          setUseEditor((prev) => {
            if (!prev) localStorage.setItem(LS_KEY_RTE, '1');
            return true;
          });
        } else {
          setNoteText(incoming);
          setNoteHtml(wrapPlainAsHtml(incoming));
        }

        const hasAdvanced = !!(n.project || n.category || (n.tags && n.tags.length) || (Array.isArray(n.images) && n.images.length));
        setShowAdvanced(hasAdvanced);
      } catch {
        Swal.fire('Error', 'Failed to load note.', 'error');
      }
    })();
  }, [noteSlug, fetchScopedLists]);

  useEffect(() => () => newPreviews.forEach(URL.revokeObjectURL), [newPreviews]);

  // Add project — CREATE FOR SELECTED USER
  const onAddProject = async (name: string) => {
    if (!userSel?._id) {
      Swal.fire('Select a user', 'Please select a user first so the item is created under that user.', 'info');
      return;
    }
    const forUser = userSel._id;
    await createProject(name, { forUser });
    const { projects } = await fetchScopedLists(forUser);
    const opt = findById(projects, projects.find((p) => p.name.toLowerCase().trim() === name.toLowerCase().trim())?._id);
    if (opt) setProjectSel(opt);
    Swal.fire('Added', `"${name}" added for ${userSel.name}.`, 'success');
  };

  // Validate & submit
  const validate = () => {
    const e: any = {};
    if (!title.trim()) e.title = 'Title is required';
    if (!userSel?._id) e.user = 'User is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const payload: NotePayload = useMemo(() => ({
    title: title.trim(),
    note: (useEditor ? noteHtml : noteText).trim(),
    entryDate: entryDate?.trim() ? new Date(entryDate).toISOString() : null,
    project: projectSel?._id || null,
    category: category || null,
    tags,
    user: userSel?._id || null,
  }), [title, noteHtml, noteText, entryDate, projectSel, category, tags, useEditor, userSel]);

  const handleSubmit = async () => {
    if (!validate()) return;

    const fd = new FormData();
    fd.append('title', payload.title);
    fd.append('note', payload.note);
    if (payload.entryDate) fd.append('entryDate', payload.entryDate);
    if (payload.project) fd.append('project', payload.project);
    if (payload.category) fd.append('category', payload.category);
    payload.tags?.forEach((t) => fd.append('tags[]', t));
    if (payload.user) fd.append('user', payload.user);
    removeFileIds.forEach((rid) => fd.append('removeImageIds[]', rid));
    newFiles.forEach((f) => fd.append('images', f));

    try {
      if (noteSlug) { await updateNote(noteSlug, fd); Swal.fire('Updated!', 'Note updated successfully.', 'success'); }
      else { await createNote(fd); Swal.fire('Created!', 'Note created successfully.', 'success'); }
      navigate('/notes');
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Something went wrong.', 'error');
    }
  };

  // UI helpers
  const SummaryChips = () => {
    const chips: string[] = [];
    if (userSel?.name) chips.push(`User: ${userSel.name}`);
    if (projectSel?.name) chips.push(`Project: ${projectSel.name}`);
    if (category) chips.push(`Category: ${category}`);
    if (tags.length) chips.push(`Tags: ${tags.join(', ')}`);
    if ((existingFiles?.length || 0) + (newFiles?.length || 0)) chips.push(`Files: ${(existingFiles.length) + (newFiles.length)}`);
    if (!chips.length) return null;
    return (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>{chips.map(c => <Chip key={c} label={c} size="small" />)}</Box>);
  };

  const acCommon = {
    freeSolo: false,
    clearOnBlur: false,
    selectOnFocus: true,
    handleHomeEndKeys: true,
    isOptionEqualToValue: (o: OptionItem, v: OptionItem) => o._id === v._id,
    getOptionLabel: (option: OptionItem) => option?.name || '',
    renderOption: (props: React.HTMLAttributes<HTMLLIElement>, option: OptionItem) => (<li {...props} key={option._id}>{option.name}</li>),
    filterSelectedOptions: true,
  } as const;

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" gutterBottom>{noteSlug ? 'Edit Note' : 'Create Note'}</Typography>

      {/* Primary fields */}
      <Stack spacing={2} mt={1}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setErrors(p => ({ ...p, title: '' })); }}
          fullWidth
          error={!!errors.title}
          helperText={errors.title}
        />

        <FormControl sx={{ maxWidth: 260 }}>
          <InputLabel id="note-category-label">Category</InputLabel>
          <Select
            labelId="note-category-label"
            label="Category"
            value={category}
            displayEmpty
            onChange={(e) => setCategory(String(e.target.value))}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {CATEGORY_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={useEditor}
              onChange={(e) => {
                const c = e.target.checked;
                if (c && !noteHtml && noteText) setNoteHtml(wrapPlainAsHtml(noteText));
                if (!c && !noteText && noteHtml) setNoteText(stripHtml(noteHtml));
                setUseEditor(c);
                localStorage.setItem(LS_KEY_RTE, c ? '1' : '0');
              }}
              color="primary"
            />
          }
          label="Use editor"
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        />

        {!useEditor ? (
          <TextField
            label="Description"
            multiline
            minRows={5}
            value={noteText}
            onChange={(e) => { setNoteText(e.target.value); setErrors(p => ({ ...p, note: '' })); }}
            fullWidth
            error={!!errors.note}
            helperText={errors.note}
          />
        ) : (
          <Box sx={(theme) => ({
            position: 'relative',
            maxHeight: 420,
            overflow: 'auto',
            borderRadius: 1,
            '& .ck-toolbar': {
              position: 'sticky', top: 0, zIndex: 3,
              backgroundColor: theme.palette.background.paper,
              borderTopLeftRadius: 4, borderTopRightRadius: 4,
            },
            '& .ck-editor__editable': { minHeight: 180, paddingBottom: 24 },
          })}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>Description</Typography>
            <CKLite value={noteHtml} onChange={(v) => { setNoteHtml(v); setErrors(p => ({ ...p, note: '' })); }} />
            {!!errors.note && (<Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.note}</Typography>)}
          </Box>
        )}

        <Box display="flex" flexWrap="wrap" gap={2}>
          <Box flex={1} minWidth={240}>
            <TextField label="Reminder" type="datetime-local" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Box>
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneRounded fontSize="small" /> More details
        </Typography>
        <Button size="small" variant="contained" color="secondary" endIcon={<ExpandMoreRounded sx={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />} onClick={() => setShowAdvanced(s => !s)}>
          {showAdvanced ? 'Hide' : 'Show'}
        </Button>
      </Box>

      {!showAdvanced && <SummaryChips />}

      <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
        <Stack spacing={2} mt={2}>
          {/* User (required) */}
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex={1} minWidth={280}>
              <Autocomplete<OptionItem>
                {...acCommon}
                options={userOptions}
                value={userSel}
                onChange={onUserChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="User (required)"
                    fullWidth
                    placeholder="Select user"
                    error={!!errors.user}
                    helperText={errors.user}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Project • Tags */}
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex={1} minWidth={280} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box flexGrow={1}>
                <Autocomplete<OptionItem>
                  {...acCommon}
                  options={projectOptions}
                  value={projectSel}
                  onChange={(_, v) => setProjectSel(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project"
                      fullWidth
                      placeholder={userSel ? (projectOptions.length ? 'Select project' : 'No projects for this user') : 'Select a user first'}
                    />
                  )}
                />
              </Box>
              <Tooltip title={userSel ? 'Add new project' : 'Select a user first'}>
                <span>
                  <IconButton onClick={() => setAddProjectOpen(true)} size="small" disabled={!userSel}><AddRounded /></IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box flex={1} minWidth={280}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={tags}
                onChange={(_, v) => setTags((v as string[]).map((t) => t.trim()).filter(Boolean))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={`#${option}`} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Tags" placeholder="Press enter to add a tag" fullWidth />}
              />
            </Box>
          </Box>

          {/* Attachments */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Attachments</Typography>

            {(existingFiles?.length ?? 0) > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mb: 1 }}>
                {existingFiles.map((f) => {
                  const isImg = (f.mimetype || '').startsWith('image/');
                  const icon = !f.mimetype ? <InsertDriveFileRounded fontSize="small" /> :
                    f.mimetype === 'application/pdf' ? <PictureAsPdfRounded fontSize="small" /> :
                    f.mimetype === 'text/plain' ? <DescriptionRounded fontSize="small" /> :
                    (f.mimetype.startsWith('image/') ? <InsertDriveFileRounded fontSize="small" /> :
                    (f.mimetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || f.mimetype === 'application/vnd.ms-excel') ? <TableChartRounded fontSize="small" /> :
                    <InsertDriveFileRounded fontSize="small" />);

                  return isImg ? (
                    <Box key={f._id} sx={{ position: 'relative', width: 110, height: 90, border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
                      <img src={toFileUrl(f.url)} alt={f.filename || 'file'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton size="small" onClick={() => { setExistingFiles(prev => prev.filter(i => i._id !== f._id)); setRemoveFileIds(prev => [...prev, f._id]); }} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                        <DeleteRounded fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Chip
                      key={f._id}
                      icon={icon}
                      label={<MLink href={toFileUrl(f.url)} underline="hover" target="_blank" rel="noopener">{f.filename || 'file'}</MLink>}
                      onDelete={() => { setExistingFiles(prev => prev.filter(i => i._id !== f._id)); setRemoveFileIds(prev => [...prev, f._id]); }}
                      variant="outlined"
                      sx={{ maxWidth: 260 }}
                    />
                  );
                })}
              </Box>
            )}

            {newPreviews.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mb: 1 }}>
                {newPreviews.map((src, i) => {
                  const f = newFiles[i]; const isImg = f?.type?.startsWith('image/');
                  return isImg ? (
                    <Box key={src} sx={{ position: 'relative', width: 110, height: 90, border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
                      <img src={src} alt={`new-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton size="small" onClick={() => {
                        setNewPreviews(prev => { const copy = [...prev]; copy.splice(i, 1); return copy; });
                        setNewFiles(prev => { const copy = [...prev]; copy.splice(i, 1); return copy; });
                      }} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                        <DeleteRounded fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Chip key={src} icon={<InsertDriveFileRounded fontSize="small" />} label={f?.name || `file-${i + 1}`} onDelete={() => {
                      setNewPreviews(prev => { const copy = [...prev]; copy.splice(i, 1); return copy; });
                      setNewFiles(prev => { const copy = [...prev]; copy.splice(i, 1); return copy; });
                    }} variant="outlined" sx={{ maxWidth: 260 }} />
                  );
                })}
              </Box>
            )}

            <Box sx={{ border: '1px dashed #bbb', p: 2, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Button startIcon={<UploadRounded />} variant="contained" color="info" component="label">
                Choose Files
                <input hidden multiple type="file"
                  onChange={(e) => {
                    const files = e.target.files; if (!files?.length) return;
                    const arr = Array.from(files);
                    setNewFiles((prev) => [...prev, ...arr]);
                    setNewPreviews((prev) => [...prev, ...arr.map((f) => URL.createObjectURL(f))]);
                  }}
                />
              </Button>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={`${existingFiles.length} existing`} size="small" />
                <Chip label={`${newFiles.length} new`} size="small" />
              </Box>
            </Box>
          </Box>
        </Stack>
      </Collapse>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" color="secondary" startIcon={<ArrowBackRounded />} onClick={() => navigate(-1)} sx={{ minWidth: 140 }}>Back</Button>
        <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
          <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>{noteSlug ? 'Update' : 'Create'}</Button>
        </Box>
      </Box>

      <AddProjectDialog
        open={addProjectOpen}
        onClose={() => setAddProjectOpen(false)}
        options={projectOptions}
        onCreate={onAddProject}
        onSelectExisting={(opt) => setProjectSel(opt)}
      />
    </Paper>
  );
};

export default NoteForm;
